/**
 * Ingredient Deduplication
 *
 * Consolidates similar ingredients by:
 * - Case-insensitive matching
 * - Singular/plural normalization
 * - Unit conversion for same measurement types
 * - Adding quantities together for matching ingredients
 *
 * Rules:
 * 1. Only merge ingredients with the same base name (case-insensitive, singular/plural normalized)
 * 2. Only merge ingredients with the same unit type (e.g., "onion" and "box of onion" stay separate)
 * 3. Convert units of the same measurement type (weight: g/lb/oz/kg, volume: ml/l/cups)
 * 4. Default quantity is 1 when no quantity specified
 * 5. Output uses singular form
 */

// ============================================================================
// Unit Conversion Constants
// ============================================================================

/**
 * Unit aliases - maps various unit representations to canonical forms
 */
const UNIT_ALIASES: Record<string, string> = {
  // Weight - Imperial
  lb: "lb",
  lbs: "lb",
  pound: "lb",
  pounds: "lb",
  oz: "oz",
  ounce: "oz",
  ounces: "oz",

  // Weight - Metric
  g: "g",
  gm: "g",
  gram: "g",
  grams: "g",
  kg: "kg",
  kgs: "kg",
  kilogram: "kg",
  kilograms: "kg",

  // Volume - Imperial
  cup: "cup",
  cups: "cup",
  tbsp: "tbsp",
  tablespoon: "tbsp",
  tablespoons: "tbsp",
  tsp: "tsp",
  teaspoon: "tsp",
  teaspoons: "tsp",
  pint: "pint",
  pints: "pint",
  quart: "quart",
  quarts: "quart",
  gallon: "gallon",
  gallons: "gallon",
  gal: "gallon",

  // Volume - Metric
  ml: "ml",
  milliliter: "ml",
  milliliters: "ml",
  l: "l",
  liter: "l",
  liters: "l",

  // Container units - each is its own type (not convertible)
  bottle: "bottle",
  bottles: "bottle",
  can: "can",
  cans: "can",
  jar: "jar",
  jars: "jar",
  box: "box",
  boxes: "box",
  bag: "bag",
  bags: "bag",
  pack: "pack",
  packs: "pack",
  package: "package",
  packages: "package",
  carton: "carton",
  cartons: "carton",
  container: "container",
  containers: "container",
  bunch: "bunch",
  bunches: "bunch",
  head: "head",
  heads: "head",
  loaf: "loaf",
  loaves: "loaf",
  dozen: "dozen",
  dozens: "dozen",

  // Chinese units - Weight
  克: "g",
  公斤: "kg",
  千克: "kg",
  斤: "斤", // Chinese catty - unique unit
  兩: "兩", // Traditional Chinese liang
  两: "兩", // Simplified Chinese liang
  磅: "lb",
  台斤: "台斤", // Taiwan catty - unique unit

  // Chinese units - Count/Measure words (each is its own type)
  個: "個",
  个: "個",
  隻: "隻",
  只: "隻",
  條: "條",
  条: "條",
  根: "根",
  把: "把",
  片: "片",
  塊: "塊",
  块: "塊",
  包: "包",
  袋: "袋",
  瓶: "瓶",
  罐: "罐",
  盒: "盒",
  聽: "聽",
  听: "聽",
  桶: "桶",
  箱: "箱",
  串: "串",
  顆: "顆",
  颗: "顆",
  粒: "粒",
  枚: "枚",
  支: "支",
  張: "張",
  张: "張",
  份: "份",
  碗: "碗",
  盤: "盤",
  盘: "盤",
  碟: "碟",
  杯: "杯",
  勺: "勺",
  匙: "匙",
  打: "打", // Chinese dozen
};

/**
 * Measurement type groups - units that can be converted between each other
 */
type MeasurementType = "weight" | "volume" | "count" | "unique";

const MEASUREMENT_TYPES: Record<string, MeasurementType> = {
  // Weight units (convertible)
  lb: "weight",
  oz: "weight",
  g: "weight",
  kg: "weight",
  斤: "weight",
  兩: "weight",
  台斤: "weight",

  // Volume units (convertible)
  cup: "volume",
  tbsp: "volume",
  tsp: "volume",
  pint: "volume",
  quart: "volume",
  gallon: "volume",
  ml: "volume",
  l: "volume",
  杯: "volume", // Chinese cup - could be volume

  // Container/count units (not convertible between each other)
  bottle: "unique",
  can: "unique",
  jar: "unique",
  box: "unique",
  bag: "unique",
  pack: "unique",
  package: "unique",
  carton: "unique",
  container: "unique",
  bunch: "unique",
  head: "unique",
  loaf: "unique",
  dozen: "unique",
  打: "unique",
  個: "unique",
  隻: "unique",
  條: "unique",
  根: "unique",
  把: "unique",
  片: "unique",
  塊: "unique",
  包: "unique",
  袋: "unique",
  瓶: "unique",
  罐: "unique",
  盒: "unique",
  聽: "unique",
  桶: "unique",
  箱: "unique",
  串: "unique",
  顆: "unique",
  粒: "unique",
  枚: "unique",
  支: "unique",
  張: "unique",
  份: "unique",
  碗: "unique",
  盤: "unique",
  碟: "unique",
  勺: "unique",
  匙: "unique",
};

/**
 * Conversion factors to base units (grams for weight, ml for volume)
 */
const WEIGHT_TO_GRAMS: Record<string, number> = {
  g: 1,
  kg: 1000,
  oz: 28.3495,
  lb: 453.592,
  斤: 500, // Chinese catty (mainland) = 500g
  兩: 50, // Chinese liang (mainland) = 50g (1/10 of a catty; Taiwan uses 37.5g)
  台斤: 600, // Taiwan catty = 600g
};

const VOLUME_TO_ML: Record<string, number> = {
  ml: 1,
  l: 1000,
  tsp: 4.929,
  tbsp: 14.787,
  cup: 236.588,
  pint: 473.176,
  quart: 946.353,
  gallon: 3785.41,
  杯: 236.588, // Treat Chinese cup as US cup
};

// ============================================================================
// Pluralization Handling
// ============================================================================

/**
 * Irregular plural forms mapping (plural → singular)
 */
const IRREGULAR_PLURALS: Record<string, string> = {
  // Unchanged plurals
  sheep: "sheep",
  deer: "deer",
  fish: "fish",
  trout: "trout",
  salmon: "salmon",
  bass: "bass",
  tuna: "tuna",
  shrimp: "shrimp",

  // Irregular plurals
  loaves: "loaf",
  halves: "half",
  calves: "calf",
  knives: "knife",
  wives: "wife",
  leaves: "leaf",
  shelves: "shelf",
  wolves: "wolf",
  thieves: "thief",
  lives: "life",

  // -es plurals
  tomatoes: "tomato",
  potatoes: "potato",
  mangoes: "mango",
  heroes: "hero",
  echoes: "echo",

  // -ies plurals
  cherries: "cherry",
  strawberries: "strawberry",
  blueberries: "blueberry",
  raspberries: "raspberry",
  blackberries: "blackberry",
  cranberries: "cranberry",
  berries: "berry",
  bodies: "body",
  candies: "candy",
  jellies: "jelly",
  patties: "patty",
  varieties: "variety",
  celeries: "celery",
  anchovies: "anchovy",
};

/**
 * Convert a word to its singular form
 */
export function toSingular(word: string): string {
  const lower = word.toLowerCase();

  // Check irregular plurals first
  if (IRREGULAR_PLURALS[lower]) {
    // Preserve original case if word was capitalized
    const singular = IRREGULAR_PLURALS[lower];
    if (word[0] === word[0].toUpperCase()) {
      return singular.charAt(0).toUpperCase() + singular.slice(1);
    }
    return singular;
  }

  // Common plural patterns (from most specific to least)

  // -ies → -y (berries → berry, but not "species")
  if (lower.endsWith("ies") && lower.length > 4) {
    // Check if it's actually a -y → -ies plural
    const singular = lower.slice(0, -3) + "y";
    return word[0] === word[0].toUpperCase()
      ? singular.charAt(0).toUpperCase() + singular.slice(1)
      : singular;
  }

  // -es endings
  if (lower.endsWith("shes") || lower.endsWith("ches") || lower.endsWith("xes")) {
    // dishes, watches, boxes → dish, watch, box
    const singular = lower.slice(0, -2);
    return word[0] === word[0].toUpperCase()
      ? singular.charAt(0).toUpperCase() + singular.slice(1)
      : singular;
  }

  if (lower.endsWith("sses") || lower.endsWith("zzes")) {
    // glasses, fuzzes → glass, fuzz
    const singular = lower.slice(0, -2);
    return word[0] === word[0].toUpperCase()
      ? singular.charAt(0).toUpperCase() + singular.slice(1)
      : singular;
  }

  if (lower.endsWith("oes")) {
    // tomatoes, potatoes → tomato, potato
    const singular = lower.slice(0, -2);
    return word[0] === word[0].toUpperCase()
      ? singular.charAt(0).toUpperCase() + singular.slice(1)
      : singular;
  }

  // General -s plural (most common)
  if (lower.endsWith("s") && !lower.endsWith("ss") && lower.length > 2) {
    const singular = lower.slice(0, -1);
    return word[0] === word[0].toUpperCase()
      ? singular.charAt(0).toUpperCase() + singular.slice(1)
      : singular;
  }

  // Not a plural, return as-is
  return word;
}

/**
 * Normalize ingredient name for comparison
 * - Lowercase
 * - Singular form
 * - Trim whitespace
 */
export function normalizeIngredientName(name: string): string {
  const trimmed = name.trim().toLowerCase();

  // Handle multi-word ingredients (normalize each word separately for plurals)
  const words = trimmed.split(/\s+/);
  if (words.length > 1) {
    // Singularize the last word (usually the noun)
    const lastWord = toSingular(words[words.length - 1]);
    return [...words.slice(0, -1), lastWord].join(" ");
  }

  return toSingular(trimmed);
}

// ============================================================================
// Unit Parsing
// ============================================================================

interface ParsedIngredient {
  quantity: number;
  unit: string | null; // Canonical unit
  originalUnit: string | null; // Original unit string for output
  name: string;
  normalizedName: string;
  rawText: string;
}

/**
 * Parse quantity from a string, handling fractions and decimals
 */
function parseQuantityValue(str: string): number {
  // Handle fractions: "1/2", "3/4"
  if (str.includes("/")) {
    const [num, denom] = str.split("/").map(Number);
    if (!isNaN(num) && !isNaN(denom) && denom !== 0) {
      return num / denom;
    }
  }

  const num = parseFloat(str);
  return isNaN(num) ? 1 : num;
}

/**
 * Parse an ingredient string into its components
 */
export function parseIngredient(text: string): ParsedIngredient {
  const rawText = text.trim();

  // Chinese units pattern for weight/measure words
  const chineseUnits =
    "克斤兩两磅公斤千克台斤個个隻只條条根把片塊块包袋瓶罐盒聽听桶箱串顆颗粒枚支張张份碗盤盘碟杯勺匙打";

  // English units - comprehensive list
  const englishUnits = [
    // Weight
    "lb",
    "lbs",
    "pound",
    "pounds",
    "oz",
    "ounce",
    "ounces",
    "g",
    "gm",
    "gram",
    "grams",
    "kg",
    "kgs",
    "kilogram",
    "kilograms",
    // Volume
    "cup",
    "cups",
    "tbsp",
    "tablespoon",
    "tablespoons",
    "tsp",
    "teaspoon",
    "teaspoons",
    "ml",
    "milliliter",
    "milliliters",
    "l",
    "liter",
    "liters",
    "pint",
    "pints",
    "quart",
    "quarts",
    "gallon",
    "gallons",
    "gal",
    // Containers
    "bottle",
    "bottles",
    "can",
    "cans",
    "jar",
    "jars",
    "box",
    "boxes",
    "bag",
    "bags",
    "pack",
    "packs",
    "package",
    "packages",
    "carton",
    "cartons",
    "container",
    "containers",
    "bunch",
    "bunches",
    "head",
    "heads",
    "loaf",
    "loaves",
    "dozen",
    "dozens",
  ].join("|");

  // Pattern for "X unit of Y" or "X unit Y"
  const englishPattern = new RegExp(
    `^(\\d+\\.?\\d*(?:\\/\\d+)?)\\s*(${englishUnits})(?:\\s+of)?\\s+(.+)$`,
    "i"
  );

  // Pattern for "Xunit Y" (no space, like "500g chicken")
  const compactEnglishPattern = new RegExp(`^(\\d+\\.?\\d*)(${englishUnits})\\s+(.+)$`, "i");

  // Pattern for Chinese: "数量单位名称" (e.g., "500克鸡肉", "2个苹果")
  const chinesePattern = new RegExp(`^(\\d+\\.?\\d*)([${chineseUnits}])\\s*(.+)$`);

  // Pattern for just a number followed by ingredient (e.g., "2 onion")
  const bareNumberPattern = /^(\d+\.?\d*(?:\/\d+)?)\s+(.+)$/;

  let quantity = 1;
  let unit: string | null = null;
  let originalUnit: string | null = null;
  let name = rawText;

  // Try English patterns
  let match = rawText.match(englishPattern);
  if (match) {
    quantity = parseQuantityValue(match[1]);
    originalUnit = match[2];
    unit = UNIT_ALIASES[match[2].toLowerCase()] || match[2].toLowerCase();
    name = match[3];
  } else {
    match = rawText.match(compactEnglishPattern);
    if (match) {
      quantity = parseQuantityValue(match[1]);
      originalUnit = match[2];
      unit = UNIT_ALIASES[match[2].toLowerCase()] || match[2].toLowerCase();
      name = match[3];
    }
  }

  // Try Chinese pattern
  if (!match) {
    match = rawText.match(chinesePattern);
    if (match) {
      quantity = parseQuantityValue(match[1]);
      originalUnit = match[2];
      unit = UNIT_ALIASES[match[2]] || match[2];
      name = match[3];
    }
  }

  // Try bare number pattern
  if (!match) {
    match = rawText.match(bareNumberPattern);
    if (match) {
      quantity = parseQuantityValue(match[1]);
      unit = null;
      originalUnit = null;
      name = match[2];
    }
  }

  return {
    quantity,
    unit,
    originalUnit,
    name: name.trim(),
    normalizedName: normalizeIngredientName(name),
    rawText,
  };
}

// ============================================================================
// Unit Conversion
// ============================================================================

/**
 * Get the measurement type of a unit
 */
function getMeasurementType(unit: string | null): MeasurementType {
  if (!unit) return "count";
  return MEASUREMENT_TYPES[unit] || "unique";
}

/**
 * Check if two units can be converted between each other
 */
function canConvert(unit1: string | null, unit2: string | null): boolean {
  // Both null means same type (count)
  if (unit1 === null && unit2 === null) return true;

  // One null and one not - can't convert
  if (unit1 === null || unit2 === null) return false;

  // Same unit - trivially convertible
  if (unit1 === unit2) return true;

  // Check measurement types
  const type1 = getMeasurementType(unit1);
  const type2 = getMeasurementType(unit2);

  // Unique types can only be converted to themselves
  if (type1 === "unique" || type2 === "unique") {
    return unit1 === unit2;
  }

  // Same measurement type (weight or volume) can be converted
  return type1 === type2;
}

/**
 * Convert a quantity from one unit to another (same measurement type)
 */
function convertQuantity(quantity: number, fromUnit: string, toUnit: string): number {
  const type = getMeasurementType(fromUnit);

  if (type === "weight") {
    const grams = quantity * (WEIGHT_TO_GRAMS[fromUnit] || 1);
    return grams / (WEIGHT_TO_GRAMS[toUnit] || 1);
  }

  if (type === "volume") {
    const ml = quantity * (VOLUME_TO_ML[fromUnit] || 1);
    return ml / (VOLUME_TO_ML[toUnit] || 1);
  }

  // Count or unique - no conversion
  return quantity;
}

// ============================================================================
// Deduplication Logic
// ============================================================================

/**
 * Create a dedupe key for an ingredient
 * Combines normalized name and unit type
 */
function createDedupeKey(parsed: ParsedIngredient): string {
  // For container units, include the unit in the key
  // so "onion" and "box of onion" remain separate
  const type = getMeasurementType(parsed.unit);
  if (type === "unique" && parsed.unit) {
    return `${parsed.normalizedName}:${parsed.unit}`;
  }
  // For weight/volume units, group by measurement type (will be converted)
  // For count (no unit), just use the name
  return `${parsed.normalizedName}:${type}`;
}

/**
 * Format a quantity for display (remove unnecessary decimals)
 */
function formatQuantity(value: number): string {
  // Round to 2 decimal places
  const rounded = Math.round(value * 100) / 100;

  // Remove unnecessary decimal places
  if (rounded % 1 === 0) {
    return rounded.toString();
  }

  // Format with up to 2 decimal places, removing trailing zeros
  return rounded.toFixed(2).replace(/\.?0+$/, "");
}

/**
 * Reconstruct ingredient text from parsed components
 */
function reconstructIngredient(
  quantity: number,
  unit: string | null,
  originalUnit: string | null,
  name: string
): string {
  const formattedQty = formatQuantity(quantity);

  // Singularize the name for output (always use singular form)
  const outputName = normalizeIngredientName(name);

  if (!unit) {
    // Count-based: "5 onion" or just "onion" for quantity of 1
    // Don't show quantity of 1 for count-based items to preserve original intention
    if (quantity === 1) {
      return outputName;
    }
    return `${formattedQty} ${outputName}`;
  }

  // Check if it's a Chinese unit
  const isChinese = /[\u4e00-\u9fff]/.test(originalUnit || unit);

  if (isChinese) {
    // Chinese format: "500g beef" or "2个苹果"
    return `${formattedQty}${originalUnit || unit} ${outputName}`;
  }

  // English format: "500g beef" or "2 boxes of onion"
  const displayUnit = originalUnit || unit;

  // Container units use "of" format
  const containerUnits = [
    "bottle",
    "can",
    "jar",
    "box",
    "bag",
    "pack",
    "package",
    "carton",
    "container",
    "bunch",
    "head",
    "loaf",
    "dozen",
  ];
  if (containerUnits.includes(unit)) {
    return `${formattedQty} ${displayUnit} of ${outputName}`;
  }

  // Weight/volume units: no space for g/kg/lb/oz, space for longer units
  const shortUnits = ["g", "kg", "lb", "lbs", "oz", "ml", "l"];
  if (shortUnits.includes(unit.toLowerCase())) {
    return `${formattedQty}${displayUnit} ${outputName}`;
  }

  // Longer units (cup, tbsp, etc.)
  return `${formattedQty} ${displayUnit} ${outputName}`;
}

interface ExistingIngredient {
  text: string;
  quantity?: string;
}

interface ExistingIngredientWithId extends ExistingIngredient {
  id: string;
}

/**
 * Result of deduplication with actions to perform
 */
export interface DedupeResult {
  /** Existing ingredients to update with new quantities */
  updates: Array<{
    id: string;
    newQuantity: string;
    unit: string | null;
  }>;
  /** New ingredients to add (already deduplicated among themselves) */
  additions: string[];
}

/**
 * Deduplicate new ingredients and return structured actions
 *
 * @param newIngredients - Array of new ingredient strings to add
 * @param existingIngredients - Array of existing ingredients in the list (with IDs)
 * @returns Object with updates to existing ingredients and new additions
 */
export function dedupeIngredientsWithActions(
  newIngredients: string[],
  existingIngredients: ExistingIngredientWithId[]
): DedupeResult {
  // Build a map of existing ingredients by dedupe key
  const existingMap = new Map<
    string,
    {
      id: string;
      quantity: number;
      unit: string | null;
      originalUnit: string | null;
      name: string;
    }
  >();

  for (const existing of existingIngredients) {
    const fullText = existing.quantity ? `${existing.quantity} ${existing.text}` : existing.text;
    const parsed = parseIngredient(fullText);
    const key = createDedupeKey(parsed);

    // If multiple existing ingredients have the same key, use the first one
    if (!existingMap.has(key)) {
      existingMap.set(key, {
        id: existing.id,
        quantity: parsed.quantity,
        unit: parsed.unit,
        originalUnit: parsed.originalUnit,
        name: parsed.name,
      });
    }
  }

  // Track updates to existing and new additions
  const updatesMap = new Map<
    string,
    {
      id: string;
      addedQuantity: number;
      unit: string | null;
      originalUnit: string | null;
      existingQuantity: number;
    }
  >();

  const newMap = new Map<
    string,
    {
      quantity: number;
      unit: string | null;
      originalUnit: string | null;
      name: string;
    }
  >();

  for (const newIngredient of newIngredients) {
    const parsed = parseIngredient(newIngredient);
    const key = createDedupeKey(parsed);

    if (existingMap.has(key)) {
      // Matches existing ingredient - accumulate update
      const existing = existingMap.get(key)!;

      if (canConvert(parsed.unit, existing.unit)) {
        const convertedQty =
          parsed.unit && existing.unit
            ? convertQuantity(parsed.quantity, parsed.unit, existing.unit)
            : parsed.quantity;

        if (updatesMap.has(key)) {
          updatesMap.get(key)!.addedQuantity += convertedQty;
        } else {
          updatesMap.set(key, {
            id: existing.id,
            addedQuantity: convertedQty,
            unit: existing.unit,
            originalUnit: existing.originalUnit,
            existingQuantity: existing.quantity,
          });
        }
      } else {
        // Can't convert - treat as new
        const uniqueKey = `${key}:${parsed.unit}`;
        if (newMap.has(uniqueKey)) {
          newMap.get(uniqueKey)!.quantity += parsed.quantity;
        } else {
          newMap.set(uniqueKey, {
            quantity: parsed.quantity,
            unit: parsed.unit,
            originalUnit: parsed.originalUnit,
            name: parsed.name,
          });
        }
      }
    } else if (newMap.has(key)) {
      // Merge with other new ingredient
      const entry = newMap.get(key)!;

      if (canConvert(parsed.unit, entry.unit)) {
        const convertedQty =
          parsed.unit && entry.unit
            ? convertQuantity(parsed.quantity, parsed.unit, entry.unit)
            : parsed.quantity;
        entry.quantity += convertedQty;
      } else {
        // Can't convert - add as separate
        const uniqueKey = `${key}:${parsed.unit}`;
        newMap.set(uniqueKey, {
          quantity: parsed.quantity,
          unit: parsed.unit,
          originalUnit: parsed.originalUnit,
          name: parsed.name,
        });
      }
    } else {
      // New unique ingredient
      newMap.set(key, {
        quantity: parsed.quantity,
        unit: parsed.unit,
        originalUnit: parsed.originalUnit,
        name: parsed.name,
      });
    }
  }

  // Build result
  const updates: DedupeResult["updates"] = [];
  for (const update of updatesMap.values()) {
    const totalQuantity = update.existingQuantity + update.addedQuantity;
    const formattedQty = formatQuantity(totalQuantity);
    const newQuantity = update.unit
      ? `${formattedQty}${update.originalUnit || update.unit}`
      : formattedQty;
    updates.push({
      id: update.id,
      newQuantity,
      unit: update.unit,
    });
  }

  const additions: string[] = [];
  for (const entry of newMap.values()) {
    additions.push(
      reconstructIngredient(entry.quantity, entry.unit, entry.originalUnit, entry.name)
    );
  }

  return { updates, additions };
}

/**
 * Deduplicate new ingredients against existing ingredients
 *
 * @param newIngredients - Array of new ingredient strings to add
 * @param existingIngredients - Array of existing ingredients in the list
 * @returns Array of deduplicated ingredient strings
 */
export function dedupeIngredients(
  newIngredients: string[],
  existingIngredients: ExistingIngredient[]
): string[] {
  // Build a map of existing ingredients by dedupe key
  const existingMap = new Map<
    string,
    { quantity: number; unit: string | null; originalUnit: string | null; name: string }
  >();

  for (const existing of existingIngredients) {
    // Reconstruct full text with quantity if available
    const fullText = existing.quantity ? `${existing.quantity} ${existing.text}` : existing.text;

    const parsed = parseIngredient(fullText);
    const key = createDedupeKey(parsed);

    if (existingMap.has(key)) {
      // Merge with existing entry
      const entry = existingMap.get(key)!;
      const convertedQty = parsed.unit
        ? convertQuantity(parsed.quantity, parsed.unit, entry.unit!)
        : parsed.quantity;
      entry.quantity += convertedQty;
    } else {
      existingMap.set(key, {
        quantity: parsed.quantity,
        unit: parsed.unit,
        originalUnit: parsed.originalUnit,
        name: parsed.name,
      });
    }
  }

  // Process new ingredients and merge with existing or each other
  const newMap = new Map<
    string,
    {
      quantity: number;
      unit: string | null;
      originalUnit: string | null;
      name: string;
      existedBefore: boolean;
    }
  >();

  for (const newIngredient of newIngredients) {
    const parsed = parseIngredient(newIngredient);
    const key = createDedupeKey(parsed);

    if (existingMap.has(key)) {
      // Merge with existing ingredient - convert to existing unit
      const existing = existingMap.get(key)!;

      if (canConvert(parsed.unit, existing.unit)) {
        const convertedQty =
          parsed.unit && existing.unit
            ? convertQuantity(parsed.quantity, parsed.unit, existing.unit)
            : parsed.quantity;

        if (newMap.has(key)) {
          newMap.get(key)!.quantity += convertedQty;
        } else {
          newMap.set(key, {
            quantity: existing.quantity + convertedQty,
            unit: existing.unit,
            originalUnit: existing.originalUnit,
            name: existing.name,
            existedBefore: true,
          });
        }
      } else {
        // Can't convert - add as new entry with modified key
        const uniqueKey = `${key}:${parsed.unit}`;
        if (newMap.has(uniqueKey)) {
          newMap.get(uniqueKey)!.quantity += parsed.quantity;
        } else {
          newMap.set(uniqueKey, {
            quantity: parsed.quantity,
            unit: parsed.unit,
            originalUnit: parsed.originalUnit,
            name: parsed.name,
            existedBefore: false,
          });
        }
      }
    } else if (newMap.has(key)) {
      // Merge with other new ingredient
      const entry = newMap.get(key)!;

      if (canConvert(parsed.unit, entry.unit)) {
        const convertedQty =
          parsed.unit && entry.unit
            ? convertQuantity(parsed.quantity, parsed.unit, entry.unit)
            : parsed.quantity;
        entry.quantity += convertedQty;
      } else {
        // Can't convert - add as separate entry
        const uniqueKey = `${key}:${parsed.unit}`;
        newMap.set(uniqueKey, {
          quantity: parsed.quantity,
          unit: parsed.unit,
          originalUnit: parsed.originalUnit,
          name: parsed.name,
          existedBefore: false,
        });
      }
    } else {
      // New unique ingredient
      newMap.set(key, {
        quantity: parsed.quantity,
        unit: parsed.unit,
        originalUnit: parsed.originalUnit,
        name: parsed.name,
        existedBefore: false,
      });
    }
  }

  // Convert back to ingredient strings
  const result: string[] = [];
  for (const entry of newMap.values()) {
    result.push(reconstructIngredient(entry.quantity, entry.unit, entry.originalUnit, entry.name));
  }

  return result;
}

/**
 * Find an existing ingredient that matches a new ingredient
 * Returns the existing ingredient ID if found, null otherwise
 */
export function findMatchingIngredient(
  newIngredient: string,
  existingIngredients: Array<{ id: string; text: string; quantity?: string }>
): { id: string; mergedQuantity: number; unit: string | null; originalUnit: string | null } | null {
  const parsed = parseIngredient(newIngredient);
  const key = createDedupeKey(parsed);

  for (const existing of existingIngredients) {
    const fullText = existing.quantity ? `${existing.quantity} ${existing.text}` : existing.text;

    const existingParsed = parseIngredient(fullText);
    const existingKey = createDedupeKey(existingParsed);

    if (key === existingKey && canConvert(parsed.unit, existingParsed.unit)) {
      // Found a match - calculate merged quantity
      const convertedQty =
        parsed.unit && existingParsed.unit
          ? convertQuantity(parsed.quantity, parsed.unit, existingParsed.unit)
          : parsed.quantity;

      return {
        id: existing.id,
        mergedQuantity: existingParsed.quantity + convertedQty,
        unit: existingParsed.unit,
        originalUnit: existingParsed.originalUnit,
      };
    }
  }

  return null;
}
